// Card wrapper con branding para las pantallas de auth (login, signup,
// forgot-password, device-verify). Centra el form, pone el logo del partner
// arriba y el footer_html abajo (si viene en el BrandingConfig).
//
// A11y:
//   - <main id="main"> es el skip target.
//   - Logo del partner tiene alt descriptivo; si falta logo_url, el texto
//     del h1 funciona como label.
//   - El toggle de tema ya no vive aca: es un icono fijo en la esquina
//     (ThemeToggle), montado a nivel de PartnerShell.
//   - footer_html viene renderizado en un <footer landmark> (BrandFooter)
//     para que los AT puedan skipearlo.

import type { ReactNode } from 'react';
import { useTheme } from '@/theme/ThemeProvider';
import BrandFooter from '@/components/BrandFooter';

interface AuthShellProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footerLinks?: ReactNode;
  /**
   * Salida del circuito (<FlowExitLink>). Va como slot del shell y no suelto en
   * el contenido para que quede SIEMPRE en el mismo lugar: ultimo elemento
   * interactivo de la pantalla, debajo de los links de paso. Si cada pagina lo
   * ubicara por su cuenta, terminaria arriba en unas y abajo en otras.
   */
  flowExit?: ReactNode;
}

export default function AuthShell({
  title,
  subtitle,
  children,
  footerLinks,
  flowExit,
}: AuthShellProps): JSX.Element {
  const { state, colorScheme } = useTheme();

  const displayName = state.status === 'ready' ? state.branding.display_name : 'AuthSys';
  const logoUrl =
    state.status === 'ready'
      ? colorScheme === 'dark' && state.branding.logo_dark_url
        ? state.branding.logo_dark_url
        : state.branding.logo_url
      : null;

  return (
    <main
      id="main"
      className="min-h-full flex flex-col items-center justify-center px-4 py-10"
    >
      <section
        aria-labelledby="auth-title"
        className="w-full max-w-md bg-surface rounded-2xl shadow-lg p-8 space-y-6"
      >
        <header className="space-y-3 text-center">
          {logoUrl ? (
            <img src={logoUrl} alt={`${displayName} logo`} className="mx-auto h-12 w-auto" />
          ) : null}
          <h1 id="auth-title" className="text-2xl font-semibold">
            {title}
          </h1>
          {subtitle ? <p className="text-sm text-text-muted">{subtitle}</p> : null}
        </header>

        {children}

        {footerLinks ? (
          <nav
            aria-label="Enlaces de autenticación"
            className="text-sm text-center text-text-muted space-x-4"
          >
            {footerLinks}
          </nav>
        ) : null}

        {flowExit}

        <BrandFooter />
      </section>
    </main>
  );
}
